import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, Card, ErrorNote, Spinner, ui } from '@/components/ui';
import { mfaService } from '@/services/api';
import { CodeInput } from './CodeInput';
import { mfaStatusKey } from './SecurityCard';

// In front of admin pages. The API refuses admin requests from a sign-in without two-step
// verification; this shows the way forward instead of an error.
export function MfaGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: mfaStatusKey, queryFn: mfaService.status });
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (status.isPending) return <Spinner />;
  if (status.error) return <ErrorNote error={status.error} />;
  if (status.data.sessionVerified) return <>{children}</>;

  if (!status.data.enabled)
    return (
      <Card title="Two-step verification required">
        <p>
          The admin area needs two-step verification. Set it up in your account, then come back.
        </p>
        <p>
          <Link to="/account">Go to My account → Two-step verification</Link>
        </p>
      </Card>
    );

  return (
    <Card title="Confirm it’s you">
      <form
        className={ui.stack}
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          mfaService
            .verify(code)
            .then(() => qc.invalidateQueries({ queryKey: mfaStatusKey }))
            .catch((err: unknown) => {
              setError(err);
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <p>Enter a code from your authenticator app to open the admin area.</p>
        <CodeInput id="mfa-gate-code" value={code} onChange={setCode} autoFocus />
        <ErrorNote error={error} />
        <div>
          <Button type="submit" disabled={busy}>
            Continue
          </Button>
        </div>
      </form>
    </Card>
  );
}
